import type { UseCase } from '@/components/onboarding/types';
import type { Language } from '@/i18n/translations';
import type { BlockType } from '@/types/workflow';

export interface ToolPreset {
  /** Existing BlockType — stable across presets so workflows remain portable. */
  type: BlockType;
  /** Human-readable label in SV/EN, used in ToolSidebar instead of labelKey. */
  label: Partial<Record<Language, string>> & { en: string };
  /** lucide-react icon name (resolved via ICON_MAP in ToolSidebar). */
  icon: string;
  /** Short hint for tooltips. */
  hint?: Partial<Record<Language, string>> & { en: string };
}

/**
 * Mapping of use-case -> which existing BlockTypes to surface and what to call them.
 * Only label + icon change; the underlying type stays stable so a workflow built as
 * "IT infrastructure" opens cleanly in any other use-case preset.
 *
 * BlockType universe:
 *   create-user | disable-mailbox | delete-m365 | send-email |
 *   webhook | add-to-group | condition | general
 */
export const TOOL_PRESETS: Record<UseCase, ToolPreset[]> = {
  'it-infrastructure': [
    { type: 'create-user', label: { sv: 'Server',     en: 'Server'   }, icon: 'server' },
    { type: 'webhook',     label: { sv: 'Router',     en: 'Router'   }, icon: 'router' },
    { type: 'send-email',  label: { sv: 'Databas',    en: 'Database' }, icon: 'database' },
    { type: 'general',     label: { sv: 'Tjänst',     en: 'Service'  }, icon: 'cloud' },
    { type: 'condition',   label: { sv: 'Brandvägg',  en: 'Firewall' }, icon: 'shield' },
    { type: 'add-to-group',label: { sv: 'Klustert',   en: 'Cluster'  }, icon: 'boxes' },
  ],
  'business-process': [
    { type: 'create-user', label: { sv: 'Start',      en: 'Start'       }, icon: 'play-circle' },
    { type: 'general',     label: { sv: 'Uppgift',    en: 'Task'        }, icon: 'square' },
    { type: 'condition',   label: { sv: 'Godkännande',en: 'Approval'    }, icon: 'check-square' },
    { type: 'send-email',  label: { sv: 'Notifiering',en: 'Notify'      }, icon: 'mail' },
    { type: 'webhook',     label: { sv: 'Integration',en: 'Integration' }, icon: 'plug' },
    { type: 'add-to-group',label: { sv: 'Avdelning',  en: 'Department'  }, icon: 'users' },
  ],
  'organization-chart': [
    { type: 'create-user', label: { sv: 'Person',      en: 'Person'      }, icon: 'user' },
    { type: 'add-to-group',label: { sv: 'Team',        en: 'Team'        }, icon: 'users' },
    { type: 'general',     label: { sv: 'Avdelning',   en: 'Department'  }, icon: 'building-2' },
    { type: 'condition',   label: { sv: 'Roll',        en: 'Role'        }, icon: 'badge' },
    { type: 'send-email',  label: { sv: 'Kontakt',     en: 'Contact'     }, icon: 'mail' },
  ],
  'decision-tree': [
    { type: 'create-user', label: { sv: 'Start',        en: 'Start'       }, icon: 'help-circle' },
    { type: 'condition',   label: { sv: 'Fråga',        en: 'Question'    }, icon: 'git-branch' },
    { type: 'general',     label: { sv: 'Resultat',     en: 'Outcome'     }, icon: 'check-circle-2' },
    { type: 'send-email',  label: { sv: 'Tips',         en: 'Hint'        }, icon: 'lightbulb' },
    { type: 'add-to-group',label: { sv: 'Kategori',     en: 'Category'    }, icon: 'folder' },
    { type: 'webhook',     label: { sv: 'Extern data',  en: 'External'    }, icon: 'link' },
  ],
  'mind-map': [
    { type: 'create-user', label: { sv: 'Central idé',  en: 'Central idea'}, icon: 'brain' },
    { type: 'general',     label: { sv: 'Gren',          en: 'Branch'     }, icon: 'git-branch' },
    { type: 'add-to-group',label: { sv: 'Tema',          en: 'Theme'      }, icon: 'tag' },
    { type: 'condition',   label: { sv: 'Not',           en: 'Note'       }, icon: 'sticky-note' },
    { type: 'send-email',  label: { sv: 'Referens',      en: 'Reference'  }, icon: 'bookmark' },
  ],
  'project-planning': [
    { type: 'create-user', label: { sv: 'Milstolpe',    en: 'Milestone'   }, icon: 'flag' },
    { type: 'general',     label: { sv: 'Uppgift',      en: 'Task'        }, icon: 'check-square' },
    { type: 'condition',   label: { sv: 'Beslut',       en: 'Decision'    }, icon: 'git-fork' },
    { type: 'add-to-group',label: { sv: 'Sprint',       en: 'Sprint'      }, icon: 'calendar' },
    { type: 'webhook',     label: { sv: 'Beroende',     en: 'Dependency'  }, icon: 'link' },
    { type: 'send-email',  label: { sv: 'Status',       en: 'Status'      }, icon: 'bell' },
  ],
  'sandbox': [
    { type: 'create-user',    label: { sv: 'Skapa anv.',    en: 'Create user'     }, icon: 'play' },
    { type: 'disable-mailbox',label: { sv: 'Stäng brevlåda',en: 'Disable mailbox' }, icon: 'message' },
    { type: 'delete-m365',    label: { sv: 'Ta bort M365',  en: 'Delete M365'     }, icon: 'monitor' },
    { type: 'send-email',     label: { sv: 'Skicka email',  en: 'Send email'      }, icon: 'bell' },
    { type: 'webhook',        label: { sv: 'Webhook',       en: 'Webhook'         }, icon: 'zap' },
    { type: 'add-to-group',   label: { sv: 'Lägg i grupp',  en: 'Add to group'    }, icon: 'users' },
    { type: 'condition',      label: { sv: 'Villkor',       en: 'Condition'       }, icon: 'branch' },
    { type: 'general',        label: { sv: 'Funktion',      en: 'Function'        }, icon: 'settings' },
  ],
};

export function getToolPreset(useCase: UseCase | null | undefined): ToolPreset[] {
  if (!useCase) return TOOL_PRESETS.sandbox;
  return TOOL_PRESETS[useCase] ?? TOOL_PRESETS.sandbox;
}
